import { useCallback, useEffect, useRef, useState } from "react";
import {
  acquireAnnotationLease, heartbeatAnnotationLease, releaseAnnotationLease, saveAnnotation,
} from "@/lib/annotatedTextQueries";

const AUTOSAVE_DEBOUNCE_MS = 800;
const HEARTBEAT_INTERVAL_MS = 20000; // renews well inside the 45s server TTL

function draftKey(atxId, beatId) {
  return `annotated_text_draft:${atxId}:${beatId}`;
}

function readDraft(atxId, beatId) {
  try {
    const raw = localStorage.getItem(draftKey(atxId, beatId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeDraft(atxId, beatId, text, baseRevision) {
  try {
    localStorage.setItem(draftKey(atxId, beatId), JSON.stringify({ text, baseRevision, savedAt: new Date().toISOString() }));
  } catch {
    // localStorage may be unavailable (private mode, quota) — draft recovery
    // is a convenience, never a correctness requirement, so this is safe to skip.
  }
}

function clearDraft(atxId, beatId) {
  try {
    localStorage.removeItem(draftKey(atxId, beatId));
  } catch {
    /* see writeDraft */
  }
}

/**
 * Master v5 Prompt 25 — one lease/autosave lifecycle per voice-annotation
 * block (one per StructureBeat). Adapted from useBlockLease.js's exact
 * lease/autosave/draft-recovery mechanics — the only structural
 * difference is the beat-keyed (not WorkDocumentBlock-keyed) lease API
 * and a plain `note_text` string instead of a generic `content` object
 * (see annotated_text_contracts.py for why this is a parallel, not a
 * shared, lease implementation).
 *
 * `block` is this beat's latest {annotation, active_lease} view from the
 * polling-refreshed session query. A remote saved update never overwrites
 * `localText` while the caller is actively editing.
 */
export function useAnnotationLease({ workspaceId, atxId, block, isViewAsClient }) {
  const beatId = block.beat_id;
  const [lease, setLease] = useState(null); // { lease_id, lease_token, expires_at }
  const [localText, setLocalText] = useState(block.annotation.note_text ?? "");
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error | conflict | locked
  const [conflictDoc, setConflictDoc] = useState(null);
  const [recoverableDraft, setRecoverableDraft] = useState(() => {
    const d = readDraft(atxId, beatId);
    if (d && d.text !== (block.annotation.note_text ?? "")) return d;
    return null;
  });

  const leaseRef = useRef(null);
  const heartbeatTimer = useRef(null);
  const saveTimer = useRef(null);
  const idempotencyKeyRef = useRef(null);
  const baseRevisionRef = useRef(block.annotation.revision);
  const danIdRef = useRef(block.annotation.dan_id);

  useEffect(() => {
    leaseRef.current = lease;
  }, [lease]);

  useEffect(() => {
    if (!dirty) {
      baseRevisionRef.current = block.annotation.revision;
      danIdRef.current = block.annotation.dan_id;
      if (!lease) setLocalText(block.annotation.note_text ?? "");
    }
  }, [block.annotation.revision, block.annotation.note_text, block.annotation.dan_id, dirty, lease]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
  }, []);

  const releasingRef = useRef(false);

  const doRelease = useCallback(async () => {
    if (releasingRef.current || !leaseRef.current) return;
    releasingRef.current = true;
    stopHeartbeat();
    const current = leaseRef.current;
    leaseRef.current = null; // guard against a second concurrent call (blur + button click) before this awaits
    setLease(null);
    try {
      await releaseAnnotationLease(workspaceId, atxId, beatId, current.lease_id, current.lease_token);
    } catch {
      // best-effort — an unreachable release simply lets the lease expire naturally
    } finally {
      releasingRef.current = false;
    }
  }, [workspaceId, atxId, beatId, stopHeartbeat]);

  useEffect(() => () => {
    if (leaseRef.current) {
      releaseAnnotationLease(workspaceId, atxId, beatId, leaseRef.current.lease_id, leaseRef.current.lease_token).catch(() => {});
    }
    stopHeartbeat();
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, [workspaceId, atxId, beatId, stopHeartbeat]);

  const startEditing = useCallback(async () => {
    if (isViewAsClient || lease) return;
    setSaveState("idle");
    try {
      const acquired = await acquireAnnotationLease(workspaceId, atxId, beatId);
      setLease(acquired);
      heartbeatTimer.current = setInterval(() => {
        heartbeatAnnotationLease(workspaceId, atxId, beatId, acquired.lease_id, acquired.lease_token).catch(() => {
          // a failed renew is surfaced on the NEXT save attempt (403), not here
        });
      }, HEARTBEAT_INTERVAL_MS);
    } catch (err) {
      if (err.status === 409) setSaveState("locked");
      else setSaveState("error");
    }
  }, [isViewAsClient, lease, workspaceId, atxId, beatId]);

  const flushSave = useCallback(async (text) => {
    const current = leaseRef.current;
    if (!current) return;
    setSaveState("saving");
    try {
      const result = await saveAnnotation(workspaceId, atxId, beatId, {
        danId: danIdRef.current, noteText: text, expectedRevision: baseRevisionRef.current,
        leaseId: current.lease_id, leaseToken: current.lease_token, idempotencyKey: idempotencyKeyRef.current,
      });
      baseRevisionRef.current = result.revision;
      setDirty(false);
      setSaveState("saved");
      setConflictDoc(null);
      clearDraft(atxId, beatId);
    } catch (err) {
      if (err.status === 409) {
        setSaveState("conflict");
        setConflictDoc(err.body?.detail?.current || null);
        writeDraft(atxId, beatId, text, baseRevisionRef.current);
      } else {
        setSaveState("error");
      }
    }
  }, [workspaceId, atxId, beatId]);

  const onChangeText = useCallback((text) => {
    setLocalText(text);
    setDirty(true);
    writeDraft(atxId, beatId, text, baseRevisionRef.current);
    if (!idempotencyKeyRef.current || saveState !== "saving") {
      idempotencyKeyRef.current = `${beatId}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushSave(text), AUTOSAVE_DEBOUNCE_MS);
  }, [atxId, beatId, saveState, flushSave]);

  const stoppingRef = useRef(false);

  const stopEditing = useCallback(async () => {
    if (stoppingRef.current || !leaseRef.current) return;
    stoppingRef.current = true;
    try {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        if (dirty) await flushSave(localText);
      }
      await doRelease();
    } finally {
      stoppingRef.current = false;
    }
  }, [dirty, localText, flushSave, doRelease]);

  const keepLocalDraft = useCallback(() => {
    if (!recoverableDraft) return;
    setLocalText(recoverableDraft.text);
    setDirty(true);
    setRecoverableDraft(null);
  }, [recoverableDraft]);

  const discardLocalDraft = useCallback(() => {
    clearDraft(atxId, beatId);
    setRecoverableDraft(null);
  }, [atxId, beatId]);

  return {
    isMine: !!lease,
    isLockedByOther: !lease && !!block.active_lease,
    localText,
    dirty,
    saveState,
    conflictDoc,
    recoverableDraft,
    startEditing,
    stopEditing,
    onChangeText,
    keepLocalDraft,
    discardLocalDraft,
    acceptLatestOnConflict: () => {
      baseRevisionRef.current = conflictDoc?.revision ?? baseRevisionRef.current;
      setLocalText(conflictDoc?.note_text ?? localText);
      setDirty(false);
      setSaveState("idle");
      setConflictDoc(null);
      clearDraft(atxId, beatId);
    },
  };
}
